import { useRef, useState } from 'react';
import { media as Media } from '@tvx/api-client';

/**
 * Attach one image to whatever is being edited.
 *
 * The picker carries the upload itself: every form that wants a picture then
 * needs one line rather than its own file input, its own uploading state and
 * its own "what is attached right now" preview — which is how the module and
 * lesson forms ended up with a `cover_media_id` the console could never set.
 *
 * It reports both the id (what gets saved) and the path (what to show), so a
 * form can put the picture back on screen without re-reading the row.
 */
export function ImagePick({ label = '', hint, path, alt = '', ratio = '16 / 9', onPick }: {
  /** Left out when the card around it already names the picture. */
  label?: string;
  hint?: string;
  path: string | null;
  /** Alt text for the upload — the title of the thing it belongs to. */
  alt?: string;
  ratio?: string;
  onPick: (mediaId: number | null, path: string | null) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  async function upload(file: File) {
    setError('');
    setBusy('Uploading…');
    try {
      const { media, saved } = await Media.upload(file, alt);
      onPick(media.id, media.path);
      setBusy(saved > 0 ? `Uploaded — ${Math.round(saved / 1024)} KB smaller after conversion` : 'Uploaded');
    } catch (e) {
      setBusy('');
      setError((e as Error).message);
    }
  }

  return (
    <div className="fld">
      {label ? <label className="lbl">{label}</label> : null}
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{
          width: 148, aspectRatio: ratio, flex: 'none', borderRadius: 12, overflow: 'hidden',
          border: path ? '1px solid var(--line)' : '1px dashed var(--line2)',
          background: 'var(--bg2, rgba(var(--wash), calc(.03 * var(--washx))))', display: 'grid', placeItems: 'center',
        }}>
          {path
            ? <img src={path} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : <span className="muted" style={{ fontSize: 11 }}>No image</span>}
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); e.target.value = ''; }} />
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <button className="b bo" type="button" onClick={() => fileRef.current?.click()}>
              {path ? 'Replace' : 'Upload'}
            </button>
            {path ? (
              <button className="b bo" type="button" onClick={() => { onPick(null, null); setBusy(''); }}>Remove</button>
            ) : null}
          </div>
          {hint ? <p className="hint" style={{ marginTop: 9 }}>{hint}</p> : null}
          {busy ? <p className="hint" style={{ marginTop: 6, color: 'var(--tx3)' }}>{busy}</p> : null}
          {error ? <p className="hint" style={{ marginTop: 6, color: 'var(--dn, #e5484d)' }}>{error}</p> : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The picture a row has, at list size.
 *
 * Always rendered, even with nothing attached: a dashed empty box is how an
 * operator sees at a glance which rows still need a picture, which a missing
 * element cannot tell them.
 */
export function Thumb({ src, width = 72, ratio = 16 / 9 }: {
  src?: string | null; width?: number; ratio?: number;
}) {
  const height = Math.round(width / ratio);
  return (
    <div style={{
      width, height, flex: 'none', borderRadius: 6, overflow: 'hidden',
      border: src ? '1px solid var(--line)' : '1px dashed var(--line2)',
      background: 'rgba(var(--wash), calc(.03 * var(--washx)))', display: 'grid', placeItems: 'center',
    }}>
      {src
        ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        : <span style={{ fontSize: 9, color: 'var(--tx3)', letterSpacing: '.04em' }}>NO IMAGE</span>}
    </div>
  );
}
